// ============================================================================
//  SUPPORT BUBBLE - Floating Notification for Unread Support Messages

// ============================================================================
// Shows a floating chat bubble in bottom-right corner ONLY when user has
// unread support messages. Clicking opens the SupportChatModal.
//
// Features:
// - Only renders when unreadCount > 0
// - Red badge with count
// - Pulse animation to draw attention
// - Click to open support modal
// - Auto-hides when messages are read
//
// Usage in App.tsx:
//   import SupportBubble from './components/SupportBubble';
//   // Inside your main layout, add:
//   <SupportBubble />
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Text,
  Portal,
  useDisclosure,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { MessageCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import supportService from '../services/supportService';
import SupportChatModal from './profile/SupportChatModal';

// ============================================================================
// ANIMATIONS
// ============================================================================

const pulseRing = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.15);
    opacity: 0.7;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const pulseGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7), 0 4px 20px rgba(0, 217, 217, 0.4);
  }
  50% {
    box-shadow: 0 0 25px 12px rgba(239, 68, 68, 0.5), 0 4px 20px rgba(0, 217, 217, 0.4);
  }
`;

const bounceIn = keyframes`
  0% {
    transform: scale(0) translateY(20px);
    opacity: 0;
  }
  50% {
    transform: scale(1.1) translateY(-5px);
  }
  100% {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
`;

const bounceOut = keyframes`
  0% {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
  100% {
    transform: scale(0) translateY(20px);
    opacity: 0;
  }
`;

// ============================================================================
// COMPONENT
// ============================================================================

const SupportBubble: React.FC = () => {
  const { currentUser } = useAuth();
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  const [unreadCount, setUnreadCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  // ============================================================================
  // SUBSCRIBE TO UNREAD COUNT
  // ============================================================================

  useEffect(() => {
    if (!currentUser?.uid) {
      setUnreadCount(0);
      setIsVisible(false);
      return;
    }

    // Subscribe to real-time unread count
    const unsubscribe = supportService.subscribeToUnreadCount(
      currentUser.uid,
      (count: number) => {
        console.log(`🔔 [SupportBubble] Unread count updated: ${count}`);
        setUnreadCount(count);
      }
    );

    return () => unsubscribe();
  }, [currentUser?.uid]);

  // ============================================================================
  // HANDLE VISIBILITY BASED ON UNREAD COUNT
  // ============================================================================
  
  useEffect(() => {
    if (unreadCount > 0 && !isVisible && !isExiting) {
      // Show bubble
      console.log('🔔 [SupportBubble] Showing bubble');
      setIsVisible(true);
    } else if (unreadCount === 0 && isVisible && !isExiting) {
      // Hide bubble with animation
      console.log('🔔 [SupportBubble] Hiding bubble');
      setIsExiting(true);
      setTimeout(() => {
        setIsVisible(false);
        setIsExiting(false);
      }, 300);
    }
  }, [unreadCount, isVisible, isExiting]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleClick = useCallback(() => {
    onOpen();
  }, [onOpen]);

  // ============================================================================
  // RENDER
  // ============================================================================

  // Don't render anything if no unread messages and not animating out
  if (!isVisible && !isExiting) {
    return (
      <>
        {/* Keep modal available for direct opens if needed */}
        <SupportChatModal isOpen={isOpen} onClose={onClose} />
      </>
    );
  }

  return (
    <>
      {/* Support Modal */}
      <SupportChatModal isOpen={isOpen} onClose={onClose} />

      {/* Floating Bubble - Using Portal to ensure it's above everything */}
      <Portal>
        <Box
          position="fixed"
          bottom={{ base: '80px', md: '24px' }}
          right={{ base: '16px', md: '24px' }}
          zIndex={9999}
          animation={isExiting 
            ? `${bounceOut} 0.3s ease-in forwards` 
            : `${bounceIn} 0.4s ease-out`
          }
          w={{ base: '56px', md: '60px' }}
          h={{ base: '56px', md: '60px' }}
        >
          {/* Pulse Ring Background - RED GLOW that expands outward */}
          <Box
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            w="80px"
            h="80px"
            borderRadius="full"
            bg="transparent"
            border="3px solid"
            borderColor="red.500"
            opacity={0.8}
            animation={`${pulseRing} 1.5s ease-in-out infinite`}
            pointerEvents="none"
          />
          
          {/* Second pulse ring for more effect */}
          <Box
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            w="90px"
            h="90px"
            borderRadius="full"
            bg="transparent"
            boxShadow="0 0 20px 5px rgba(239, 68, 68, 0.5)"
            animation={`${pulseRing} 1.5s ease-in-out infinite 0.3s`}
            pointerEvents="none"
          />

          {/* Main Button */}
          <Box
            as="button"
            onClick={handleClick}
            w="100%"
            h="100%"
            borderRadius="full"
            bg="linear-gradient(135deg, #00D9D9 0%, #0D4F4F 100%)"
            display="flex"
            alignItems="center"
            justifyContent="center"
            cursor="pointer"
            position="relative"
            zIndex={1}
            boxShadow="0 0 15px 5px rgba(239, 68, 68, 0.6), 0 4px 20px rgba(0, 217, 217, 0.4)"
            animation={`${pulseGlow} 2s ease-in-out infinite`}
            transition="all 0.2s ease"
            _hover={{
              transform: 'scale(1.1)',
              boxShadow: '0 0 20px 8px rgba(239, 68, 68, 0.7), 0 6px 25px rgba(0, 217, 217, 0.6)',
            }}
            _active={{
              transform: 'scale(0.95)',
            }}
            aria-label={`${unreadCount} unread support messages`}
          >
            <MessageCircle size={28} color="white" />
          </Box>
          
          {/* Unread Badge */}
          <Box
            position="absolute"
            top="0px"
            right="0px"
            minW="22px"
            h="22px"
            px="5px"
            borderRadius="full"
            bg="red.500"
            display="flex"
            alignItems="center"
            justifyContent="center"
            boxShadow="0 2px 8px rgba(239, 68, 68, 0.6)"
            border="2px solid #0D0D0D"
            zIndex={2}
          >
            <Text
              color="white"
              fontSize="xs"
              fontWeight="bold"
              lineHeight="1"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Text>
          </Box>

          {/* Tooltip on hover */}
          <Box
            position="absolute"
            bottom="100%"
            right="0"
            mb="8px"
            px="12px"
            py="6px"
            bg="gray.800"
            borderRadius="lg"
            boxShadow="lg"
            opacity={0}
            transform="translateY(10px)"
            transition="all 0.2s ease"
            pointerEvents="none"
            whiteSpace="nowrap"
            sx={{
              '[aria-label]:hover + &, button:hover ~ &': {
                opacity: 1,
                transform: 'translateY(0)',
              },
            }}
            _groupHover={{
              opacity: 1,
              transform: 'translateY(0)',
            }}
          >
            <Text color="white" fontSize="sm" fontWeight="medium">
              {unreadCount} new support {unreadCount === 1 ? 'message' : 'messages'}
            </Text>
            {/* Arrow */}
            <Box
              position="absolute"
              bottom="-6px"
              right="24px"
              w="0"
              h="0"
              borderLeft="6px solid transparent"
              borderRight="6px solid transparent"
              borderTop="6px solid"
              borderTopColor="gray.800"
            />
          </Box>
        </Box>
      </Portal>
    </>
  );
};

export default SupportBubble;